import React from "react";
import { Card, CardContent, Typography, Box, Tooltip } from "@mui/material";

type ExpenseRowData = {
  id: number;
  reporting_date: string;
  city_category: string;
  brick_type: string;
  total_distance_travel_km: string;
  daily_allowance: string;
  traveling_allowance: string;
  fix_allowance: string;
  food_allowance: string;
  remark: string;
  status: string;
  expected_amount: string;
  approved_status: string;
  final_amount: string;
  system_remarks: string;
  brick_name: string;
  system_calculated_amount: string;
  approved_by: number | null;
  approver_name: string | null;
};

type MonthlyExpense = {
  user_id: number;
  month_year: number;
  total_distance_travel_km: string;
  total_daily_allowance: string;
  total_traveling_allowance: string;
  total_fix_allowance: string;
  total_food_allowance: string;
  system_calculated_amount: string;
  final_amount: string;
  total_hill_station_allowance: string;
  total_missed_working_days: string;
  total_working_days: number;
  total_expense_amount: string;
  approver_name: string;
};

type UserExpenseSummary = {
  user_id: number;
  user_name: string;
  is_remark_locked: boolean;
  is_remark_submitted: boolean;
  max_expense_limit: string;
  daily_expenses: ExpenseRowData[];
  monthly_expense: MonthlyExpense[];
};

type ExpenseApiResponses = {
  success: boolean;
  user_expense_summary: UserExpenseSummary;
};

interface ExpenseSummaryCardProps {
  month: number;
  year: number;
  data: ExpenseApiResponses | null;
}

const ExpenseSummaryCard: React.FC<ExpenseSummaryCardProps> = ({
  month,
  year,
  data,
}) => {
  const monthly = data?.user_expense_summary?.monthly_expense?.[0];

  if (!monthly) return null;

  const summaryItems: {
    label: string;
    value: string | number;
    tooltip: string;
  }[] = [
    {
      label: "Total Distance",
      value: monthly.total_distance_travel_km,
      tooltip: "Total Distance",
    },
    {
      label: "Total DA",
      value: monthly.total_daily_allowance,
      tooltip: "Daily Allowance (DA)",
    },
    {
      label: "Total TA",
      value: monthly.total_traveling_allowance,
      tooltip: "Traveling Allowance (TA)",
    },
    {
      label: "Fixed Allowance",
      value: monthly.total_fix_allowance,
      tooltip: "Fixed Allowance",
    },
    {
      label: "Food Allowance",
      value: monthly.total_food_allowance,
      tooltip: "Food Allowance",
    },
    {
      label: "Hill Station Allowance",
      value: monthly.total_hill_station_allowance ?? "-",
      tooltip: "Hill Station Allowance",
    },
    {
      label: "Total Work Days",
      value: monthly.total_working_days,
      tooltip: "Total Work Days",
    },
    {
      label: "Total Missing Work Days",
      value: monthly.total_missed_working_days,
      tooltip: "Total Missing Work Days",
    },
    {
      label: "System Total Amount",
      value: Number(monthly.system_calculated_amount).toFixed(2),
      tooltip: "System Total Amount",
    },
    {
      label: "Total Final Amount",
      value: Number(monthly.final_amount).toFixed(2),
      tooltip: "Total Final Amount",
    },
    {
      label: "Total Expense Amount",
      value: Number(monthly.total_expense_amount).toFixed(2),
      tooltip: "Total Expense Amount",
    },
  ];

  return (
    <Box display="flex" justifyContent="center" mb={2}>
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          backgroundColor: "#f5f5f5",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <CardContent sx={{ py: 1 }}>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1.5}
            justifyContent={"space-around"}
          >
            {summaryItems.map((item, idx) => (
              <Box key={idx} textAlign="center">
                <Tooltip title={item.tooltip} arrow>
                  <Typography
                    variant="caption"
                    sx={{ color: "black", fontWeight: 600 }}
                  >
                    {item.label}
                  </Typography>
                </Tooltip>
                <Typography
                  variant="body2"
                  sx={{ color: "text.primary", fontWeight: 400 }}
                >
                  {item.value ?? 0}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExpenseSummaryCard;
